import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { Form, Col, Row } from "react-bootstrap";
import { NumericFormat as NumberFormat } from "react-number-format";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { ReactTags } from "react-tag-autocomplete";

import { withNavigation } from "../../../utils/wrapper";
import { deepEqual, toDateOrNull } from "../../../utils/object";
import { format as formatDate } from "../../../utils/date";
import { currency } from "../../../utils/number";
import { ConnectedSettingList } from "../../../layout/select/setting-list";
import { get as getSchedules } from "../../../actions/pay.schedules";
import { get as getSetting } from "../../../actions/settings";
import { search as searchAddresses } from "../../../actions/addresses";
import { get as getRates } from "../../../actions/rates";
import { search as searchRoles } from "../../../actions/roles";
import { get as getStaff } from "../../../actions/staff";

const normalizeRolesFromProps = (props) => {
  if (Array.isArray(props.roleUuids)) {
    return [...new Set(props.roleUuids.filter(Boolean))];
  }

  if (Array.isArray(props.roles)) {
    return [
      ...new Set(
        props.roles
          .map((r) => r?.uuid ?? r?.value ?? null)
          .filter(Boolean)
      ),
    ];
  }

  return [];
};

const buildRoleTagsFromProps = (props) => {
  if (Array.isArray(props.roles) && props.roles.length) {
    return props.roles
      .map((r) => {
        const uuid = r?.uuid ?? r?.value ?? null;
        const label = r?.caption ?? r?.name ?? r?.label ?? uuid;
        return uuid ? { value: uuid, label } : null;
      })
      .filter(Boolean);
  }

  if (Array.isArray(props.roleUuids) && props.roleUuids.length) {
    return props.roleUuids
      .filter(Boolean)
      .map((uuid) => ({ value: uuid, label: uuid }));
  }

  return [];
};

const normalizeAutoSharesFromProps = (props) => {
  const meta = props.meta && typeof props.meta === "object" ? props.meta : {};
  const items = Array.isArray(meta.autoShares)
    ? meta.autoShares
    : Array.isArray(meta.fileAutoShares)
      ? meta.fileAutoShares
      : [];

  return [
    ...new Set(
      items
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") return item.personUuid ?? item.uuid ?? item.value ?? null;
          return null;
        })
        .filter(Boolean)
    ),
  ];
};

const buildAutoShareTagsFromProps = (props) => {
  const autoShares = normalizeAutoSharesFromProps(props);

  return autoShares.map((uuid) => ({ value: uuid, label: uuid }));
};

const staffLabel = (item = {}) => {
  const person = item.Person || {};
  const firstName = item.firstName ?? person.firstName ?? "";
  const lastName = item.lastName ?? person.lastName ?? "";
  const displayName = item.displayName ?? person.displayName ?? "";
  const email = item.email ?? person.email ?? "";
  const title = item.title ? `, ${item.title}` : "";
  const name = displayName || [firstName, lastName].filter(Boolean).join(" ");

  return `${name || email || item.uuid}${title}`;
};

const locationLabel = (address = {}) => {
  const line1 = String(address.line1 ?? "").trim();
  const city = String(address.city ?? "").trim();
  const state = String(address.state ?? "").trim();
  const locale = [city, state].filter(Boolean).join(", ");

  return locale ? `${line1 || "Address"} (${locale})` : line1 || address.uuid || "Address";
};

const mapPropsToState = (props) => ({
  number: props.number ?? "",
  title: props.title ?? "",
  hireDate: toDateOrNull(props.hireDate) ?? new Date(),
  termDate: toDateOrNull(props.termDate),
  status: props.status ?? "active",
  payType: props.payType ?? "salary",
  payRate: props.payRate ?? 0.0,
  locationUuid: props.locationUuid ?? null,
  payScheduleUuid: props.payScheduleUuid ?? null,
  overtimeEligible: Boolean(props.overtimeEligible),
  meta:
    props.meta && typeof props.meta === "object" && !Array.isArray(props.meta)
      ? props.meta
      : {},
  rateCode: props.rateCode ?? null,
  roles: normalizeRolesFromProps(props),
  roleTags: buildRoleTagsFromProps(props),
  autoShares: normalizeAutoSharesFromProps(props),
  autoShareTags: buildAutoShareTagsFromProps(props),
});

const Staff = forwardRef(function Staff(props, ref) {
  const mappedProps = useMemo(
    () => mapPropsToState(props),
    [
      props.number,
      props.title,
      props.hireDate,
      props.termDate,
      props.status,
      props.payType,
      props.payRate,
      props.locationUuid,
      props.payScheduleUuid,
      props.overtimeEligible,
      props.meta,
      props.rateCode,
      props.roles,
      props.roleUuids,
      props.uuid,
    ]
  );

  const [staff, setStaff] = useState(mappedProps);
  const [options, setOptions] = useState({
    schedules: [],
    locations: [],
    rates: [],
    roles: [],
    staff: [],
  });

  const prevMappedRef = useRef(mappedProps);
  const initialValueRef = useRef(mappedProps);

  const permitted = useCallback(
    (named) => {
      const list = Array.isArray(named) ? named : [named];
      return props.auth?.permissions?.some((item) => list.includes(item));
    },
    [props.auth]
  );

  useEffect(() => {
    if (deepEqual(prevMappedRef.current, mappedProps)) return;

    setStaff(mappedProps);
    prevMappedRef.current = mappedProps;
    initialValueRef.current = mappedProps;
  }, [mappedProps]);

  const updateField = useCallback((name, value) => {
    if (!name) return;
    setStaff((prev) => ({ ...prev, [name]: value }));
  }, []);

  const onChange = useCallback(
    (e) => {
      const name = e?.target?.name;
      const value = e?.target?.value;
      updateField(name, value);
    },
    [updateField]
  );

  const normalizeRoleTag = useCallback((tag) => {
    if (!tag) return null;

    const value = String(tag?.value ?? "").trim();
    const label = String(tag?.label ?? value).trim();

    if (!value) return null;
    return { value, label: label || value };
  }, []);

  const writeRolesFromTags = useCallback((roleTags) => {
    const roles = [...new Set((roleTags || []).map((t) => t?.value).filter(Boolean))];
    setStaff((prev) => ({
      ...prev,
      roles,
      roleTags,
    }));
  }, []);

  const onRoleDelete = useCallback((index) => {
    setStaff((prev) => {
      const roleTags = (prev.roleTags || []).filter((_, idx) => idx !== index);
      const roles = [...new Set(roleTags.map((t) => t?.value).filter(Boolean))];
      return { ...prev, roleTags, roles };
    });
  }, []);

  const onRoleAdd = useCallback((tag) => {
    const normalized = normalizeRoleTag(tag);
    if (!normalized) return;

    setStaff((prev) => {
      const exists = (prev.roleTags || []).some(
        (t) => String(t.value).toLowerCase() === String(normalized.value).toLowerCase()
      );

      if (exists) return prev;

      const roleTags = [...(prev.roleTags || []), normalized];
      const roles = [...new Set(roleTags.map((t) => t?.value).filter(Boolean))];
      return { ...prev, roleTags, roles };
    });
  }, [normalizeRoleTag]);

  const normalizePersonTag = useCallback((tag) => {
    if (!tag) return null;

    const value = String(tag?.value ?? "").trim();
    const label = String(tag?.label ?? value).trim();

    if (!value) return null;
    return { value, label: label || value };
  }, []);

  const onAutoShareDelete = useCallback((index) => {
    setStaff((prev) => {
      const autoShareTags = (prev.autoShareTags || []).filter((_, idx) => idx !== index);
      const autoShares = [...new Set(autoShareTags.map((t) => t?.value).filter(Boolean))];
      const meta = { ...(prev.meta || {}), autoShares };

      return { ...prev, autoShareTags, autoShares, meta };
    });
  }, []);

  const onAutoShareAdd = useCallback((tag) => {
    const normalized = normalizePersonTag(tag);
    if (!normalized) return;

    setStaff((prev) => {
      const exists = (prev.autoShareTags || []).some(
        (t) => String(t.value).toLowerCase() === String(normalized.value).toLowerCase()
      );

      if (exists) return prev;

      const autoShareTags = [...(prev.autoShareTags || []), normalized];
      const autoShares = [...new Set(autoShareTags.map((t) => t?.value).filter(Boolean))];
      const meta = { ...(prev.meta || {}), autoShares };

      return { ...prev, autoShareTags, autoShares, meta };
    });
  }, [normalizePersonTag]);

  useEffect(() => {
    let active = true;

    const loadData = async () => {
      const schedules = await props.getSchedules();
      const setting = await props.getSetting({
        authentic: true,
        name: "FIRM_LOCATIONS",
      });
      const locations = await props.searchAddresses({
        search: setting?.value,
        size: 1000,
      });
      const rates = await props.getRates({ style: "H" });
      const rolesResult = await props.searchRoles({
        page: 0,
        size: 500,
        order: "caption",
        direction: "ASC",
        search: "",
      });
      const staffResult = await props.getStaff({});

      if (!active) return;

      const roleOptions = (rolesResult?.items ?? []).map((r) => ({
        ...r,
        value: r.uuid,
        label: r.caption || r.name,
      }));
      const staffItems = Array.isArray(staffResult) ? staffResult : [];
      const staffOptions = staffItems
        .filter((item) => item?.uuid && item.uuid !== props.uuid)
        .map((item) => ({
          ...item,
          value: item.uuid,
          label: staffLabel(item),
        }));

      setOptions({
        rates: (rates?.items ?? []).map((s) => ({
          ...s,
          value: s.code,
          label: `${s.name} (i.e. ${currency(s.value)}/hr.)`,
        })),
        locations: (locations?.items ?? []).map((s) => ({
          ...s,
          value: s.uuid,
          label: locationLabel(s),
        })),
        schedules: (schedules ?? []).map((s) => ({
          ...s,
          value: s.uuid,
          label: `${s.name} [${s.frequency} starting ${formatDate(
            s.anchorDate
          )}]`,
        })),
        roles: roleOptions,
        staff: staffOptions,
      });

      setStaff((prev) => {
        const nextTags = (prev.roleTags || []).map((tag) => {
          const found = roleOptions.find((opt) => opt.value === tag.value);
          return found ? { value: found.value, label: found.label } : tag;
        });

        const nextRoles = [...new Set(nextTags.map((t) => t?.value).filter(Boolean))];
        const nextAutoShareTags = (prev.autoShareTags || []).map((tag) => {
          const found = staffOptions.find((opt) => opt.value === tag.value);
          return found ? { value: found.value, label: found.label } : tag;
        });
        const nextAutoShares = [...new Set(nextAutoShareTags.map((t) => t?.value).filter(Boolean))];

        return {
          ...prev,
          roleTags: nextTags,
          roles: nextRoles,
          autoShareTags: nextAutoShareTags,
          autoShares: nextAutoShares,
          meta: {
            ...(prev.meta || {}),
            autoShares: nextAutoShares,
          },
        };
      });
    };

    loadData();

    return () => {
      active = false;
    };
  }, [props.getSchedules, props.getSetting, props.searchAddresses, props.getRates, props.searchRoles, props.getStaff, props.uuid]);

  const selectedSchedule =
    options.schedules.find((o) => o.value === staff.payScheduleUuid) || null;
  const selectedLocation =
    options.locations.find((o) => o.value === staff.locationUuid) || null;
  const selectedRate =
    options.rates.find((o) => o.value === staff.rateCode) || null;

  const availableRoleSuggestions = options.roles.filter(
    (role) => !(staff.roles || []).includes(role.uuid)
  );
  const availableAutoShareSuggestions = options.staff.filter(
    (item) => !(staff.autoShares || []).includes(item.uuid)
  );

  const validate = useCallback(() => {
    const errors = {};

    if (!String(staff.title ?? "").trim()) {
      errors.title = "Title is required.";
    }

    if (!staff.hireDate) {
      errors.hireDate = "Hire date is required.";
    }

    if (staff.termDate && staff.hireDate && staff.termDate < staff.hireDate) {
      errors.termDate = "Termination date cannot be before hire date.";
    }

    if (staff.payRate != null && Number(staff.payRate) < 0) {
      errors.payRate = "Pay rate cannot be negative.";
    }

    return {
      success: Object.keys(errors).length === 0,
      errors,
    };
  }, [staff]);

  useImperativeHandle(
    ref,
    () => ({
      getValue: () => ({
        ...staff,
        roles: [...new Set((staff.roleTags || []).map((t) => t?.value).filter(Boolean))],
        meta: {
          ...(staff.meta || {}),
          autoShares: [...new Set((staff.autoShareTags || []).map((t) => t?.value).filter(Boolean))],
        },
      }),
      setValue: (value = {}) => {
        const next = mapPropsToState(value);
        setStaff(next);
      },
      reset: (value = null) => {
        const next = value ? mapPropsToState(value) : initialValueRef.current;
        setStaff(next);
      },
      validate,
      isDirty: () => !deepEqual(initialValueRef.current, staff),
    }),
    [staff, validate]
  );

  return (
    <>
      <Col xs="12">
        <Row className="g-3">
          <Col md="6" lg="8">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="title"
                value={staff.title}
                onChange={onChange}
              />
              <Form.Label>TITLE</Form.Label>
            </div>
          </Col>

          {permitted("EDIT.PAYROLL") ? (
            <>
              <Col md="3" lg="2">
                <div className="mb-3 top-label">
                  <DatePicker
                    id="hireDate"
                    name="hireDate"
                    className="form-control"
                    selected={staff.hireDate}
                    onChange={(date) => updateField("hireDate", date)}
                  />
                  <Form.Label>HIRED</Form.Label>
                </div>
              </Col>
              <Col md="3" lg="2">
                <div className="mb-3 top-label">
                  <DatePicker
                    isClearable
                    id="termDate"
                    name="termDate"
                    className="form-control"
                    selected={staff.termDate}
                    onChange={(date) => updateField("termDate", date)}
                  />
                  <Form.Label>TERMINATED</Form.Label>
                </div>
              </Col>
            </>
          ) : (
            <>
              <Col md="3" lg="2">
                <div className="mb-3 top-label">
                  <Form.Control
                    type="text"
                    readOnly
                    className="form-control"
                    value={formatDate(staff.hireDate)}
                  />
                  <Form.Label>HIRED</Form.Label>
                </div>
              </Col>
              <Col md="3" lg="2">
                <div className="mb-3 top-label">
                  <Form.Control
                    type="text"
                    readOnly
                    className="form-control"
                    value={formatDate(staff.termDate)}
                  />
                  <Form.Label>TERMINATED</Form.Label>
                </div>
              </Col>
            </>
          )}
        </Row>

        {permitted("EDIT.PAYROLL") ? (
          <>
            <Row className="g-3">
              <Col md="4">
                <div className="mb-3 top-label">
                  <ConnectedSettingList
                    name="status"
                    setting="STAFF_STATUS"
                    category="Collections"
                    value={staff.status ?? ""}
                    onChange={onChange}
                  />
                  <Form.Label>STATUS</Form.Label>
                </div>
              </Col>

              <Col md="4">
                <div className="mb-3 top-label">
                  <NumberFormat
                    displayType="input"
                    type="text"
                    id="payRate"
                    name="payRate"
                    className="text-end form-control mb-2"
                    prefix="$"
                    thousandSeparator={false}
                    allowNegative={false}
                    value={staff.payRate ?? 0}
                    onValueChange={(e) =>
                      updateField("payRate", e?.floatValue ?? 0)
                    }
                    isAllowed={({ floatValue }) =>
                      floatValue == null || floatValue <= 5000000
                    }
                  />
                  <Form.Label>PAY RATE</Form.Label>
                </div>
              </Col>

              <Col md="4">
                <div className="mb-3 top-label">
                  <ConnectedSettingList
                    name="payType"
                    setting="STAFF_PAY_TYPE"
                    category="Collections"
                    value={staff.payType ?? ""}
                    onChange={onChange}
                  />
                  <Form.Label>PAY TYPE</Form.Label>
                </div>
              </Col>
            </Row>

            <Row className="g-3">
              <Col>
                <div className="mb-3 top-label">
                  <Select
                    name="payScheduleUuid"
                    id="payScheduleUuid"
                    classNamePrefix="react-select"
                    placeholder="Pay Schedule"
                    value={selectedSchedule}
                    options={options.schedules}
                    onChange={(opt) =>
                      updateField("payScheduleUuid", opt?.value ?? null)
                    }
                  />
                  <Form.Label>PAY SCHEDULE</Form.Label>
                </div>
              </Col>
            </Row>
          </>
        ) : (
          ""
        )}

        <Row className="g-3">
          <Col>
            <div className="mb-3 top-label">
              <Select
                name="locationUuid"
                id="locationUuid"
                classNamePrefix="react-select"
                placeholder="Work Location"
                value={selectedLocation}
                options={options.locations}
                onChange={(opt) => updateField("locationUuid", opt?.value ?? null)}
              />
              <Form.Label>PRIMARY LOCATION</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col>
            <div className="mb-3 top-label">
              <Select
                name="rateCode"
                id="rateCode"
                classNamePrefix="react-select"
                placeholder="Default Rate"
                value={selectedRate}
                options={options.rates}
                onChange={(opt) => updateField("rateCode", opt?.value ?? null)}
              />
              <Form.Label>DEFAULT CHARGE RATE</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-3 mt-2">
          <Col lg="12">
            <h2 className="small-title">File Auto-Shares</h2>
          </Col>
        </Row>

        <Row className="g-3">
          <Col lg="12">
            <div className="mb-3 top-label">
              <ReactTags
                selected={staff.autoShareTags || []}
                suggestions={availableAutoShareSuggestions.map((item) => ({
                  label: item.label,
                  value: item.uuid,
                }))}
                allowNew={false}
                onDelete={onAutoShareDelete}
                onAdd={onAutoShareAdd}
                placeholderText={
                  (staff.autoShareTags || []).length
                    ? ""
                    : "Add staff member"
                }
                activateFirstOption
                collapseOnSelect
                delimiterKeys={["Enter", "Tab", ","]}
                newOptionText="%value%"
              />
              <Form.Label>AUTO-SHARE UPLOADED FILES WITH</Form.Label>
            </div>
          </Col>
        </Row>

        {permitted(["VIEW.ROLE", "EDIT.ROLE"]) ? (
          <>
            <Row className="g-3 mt-2">
              <Col lg="12">
                <h2 className="small-title">Roles</h2>
              </Col>
            </Row>

            <Row className="g-3">
              <Col lg="12">
                <div className="mb-3 top-label">
                  <ReactTags
                    selected={staff.roleTags || []}
                    suggestions={availableRoleSuggestions.map((role) => ({
                      label: role.caption || role.name,
                      value: role.uuid,
                    }))}
                    allowNew={false}
                    onDelete={permitted("EDIT.ROLE") ? onRoleDelete : undefined}
                    onAdd={permitted("EDIT.ROLE") ? onRoleAdd : undefined}
                    placeholderText={
                      permitted("EDIT.ROLE")
                        ? "Add staff role"
                        : (staff.roleTags || []).length
                          ? ""
                          : "No roles assigned"
                    }
                    activateFirstOption
                    collapseOnSelect
                    delimiterKeys={["Enter", "Tab", ","]}
                    newOptionText="%value%"
                    readOnly={!permitted("EDIT.ROLE")}
                  />
                  <Form.Label>STAFF ROLES</Form.Label>
                </div>
              </Col>
            </Row>
          </>
        ) : null}
      </Col>
    </>
  );
});

Staff.propTypes = {
  auth: PropTypes.object.isRequired,
  getSchedules: PropTypes.func.isRequired,
  getSetting: PropTypes.func.isRequired,
  getRates: PropTypes.func.isRequired,
  searchAddresses: PropTypes.func.isRequired,
  searchRoles: PropTypes.func.isRequired,
  getStaff: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  uuid: PropTypes.string,
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  hireDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  termDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  status: PropTypes.string,
  payType: PropTypes.string,
  payRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  locationUuid: PropTypes.string,
  payScheduleUuid: PropTypes.string,
  overtimeEligible: PropTypes.bool,
  meta: PropTypes.object,
  rateCode: PropTypes.string,
  roles: PropTypes.array,
  roleUuids: PropTypes.array,
};

Staff.defaultProps = {
  onChange: null,
  number: "",
  title: "",
  hireDate: null,
  termDate: null,
  status: "active",
  payType: "salary",
  payRate: 0,
  locationUuid: null,
  payScheduleUuid: null,
  overtimeEligible: false,
  meta: {},
  rateCode: null,
  roles: [],
  roleUuids: [],
  uuid: null,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default withNavigation(
  connect(
    mapStateToProps,
    {
      getSchedules,
      getSetting,
      getRates,
      searchAddresses,
      searchRoles,
      getStaff,
    },
    null,
    { forwardRef: true }
  )(Staff)
);
